import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { CheckCircle, Crown, Zap, Star, ArrowRight, Users, Building2, Rocket, Shield, Gift, AlertTriangle, Calendar } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { API } from '../config/api';



const TIER_ICONS = {
  starter: Zap,
  growth: Rocket,
  pro: Star,
  elite: Crown,
  executive: Shield,
  label: Building2
};

const TIER_COLORS = {
  starter: 'bg-primary',
  growth: 'bg-blue-500',
  pro: 'bg-secondary',
  elite: 'bg-accent text-background',
  executive: 'bg-purple-500',
  label: 'bg-pink-500'
};

export default function PricingPage() {
  const { isAuthenticated, user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [hasUsedTrial, setHasUsedTrial] = useState(false);

  useEffect(() => {
    fetchTiers();
    if (isAuthenticated) {
      checkTrialStatus();
    }
  }, [isAuthenticated]);

  const fetchTiers = async () => {
    try {
      const response = await fetch(`${API}/subscriptions/tiers`);
      if (response.ok) {
        const data = await response.json();
        setTiers(data.tiers);
      }
    } catch (error) {
      console.error('Failed to fetch tiers:', error);
    }
  };

  const checkTrialStatus = async () => {
    try {
      const response = await fetch(`${API}/subscriptions/trial-status`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setHasUsedTrial(data.has_used_trial);
      }
    } catch (error) {
      console.error('Failed to check trial status:', error);
    }
  };

  const TIER_ORDER = ['starter', 'growth', 'pro', 'elite', 'executive', 'label', 'enterprise'];

  const isHigherTier = (tierId) => {
    if (!user?.artist_tier) return true;
    return TIER_ORDER.indexOf(tierId) > TIER_ORDER.indexOf(user.artist_tier);
  };

  const handleSubscribeClick = (tierId) => {
    if (!isAuthenticated) {
      toast.error('Please sign in to subscribe');
      navigate('/login');
      return;
    }

    // Existing subscriber upgrading
    if (user?.subscription_active && user?.artist_tier && isHigherTier(tierId)) {
      setSelectedTier(tierId);
      setAgreedToTerms(false);
      setShowTrialModal(true);
      return;
    }

    setSelectedTier(tierId);
    setAgreedToTerms(false);
    setShowTrialModal(true);
  };

  const handleConfirmSubscription = async () => {
    if (!agreedToTerms) {
      toast.error('You must agree to the terms to continue');
      return;
    }

    setLoading(selectedTier);
    setShowTrialModal(false);

    // Use upgrade endpoint for existing subscribers upgrading
    const isUpgrading = user?.subscription_active && user?.artist_tier && isHigherTier(selectedTier);
    
    try {
      let response;
      if (isUpgrading) {
        response = await fetch(`${API}/subscriptions/upgrade`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ tier: selectedTier })
        });
      } else {
        response = await fetch(`${API}/subscriptions/checkout?tier=${selectedTier}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ 
            origin_url: window.location.origin,
            agreed_to_terms: true 
          })
        });
      }

      if (response.ok) {
        const data = await response.json();
        if (isUpgrading && data.checkout_url) {
          window.location.href = data.checkout_url;
        } else if (data.has_trial) {
          toast.success(`Starting your 7-day free trial! You won't be charged until ${new Date(data.trial_ends_at).toLocaleDateString()}`);
          window.location.href = data.url;
        } else if (data.url) {
          window.location.href = data.url;
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create checkout session');
      }
    } catch (error) {
      toast.error('Failed to process subscription');
    } finally {
      setLoading(null);
    }
  };

  const isCurrentTier = (tierId) => {
    if (!user?.artist_tier || !user?.subscription_active) return false;
    // Handle enterprise/label alias
    const userTier = user.artist_tier;
    if ((userTier === 'label' && tierId === 'enterprise') || (userTier === 'enterprise' && tierId === 'label')) return true;
    return userTier === tierId;
  };

  const selectedTierData = tiers.find(t => t.tier_id === selectedTier);

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="pricing-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-secondary/20 text-secondary">Artist Subscriptions</Badge>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase mb-4">
            Choose Your <span className="text-secondary">Tier</span>
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Artist accounts require a paid subscription. Listeners can browse and stream for free.
          </p>
          
          {/* Free Trial Banner */}
          {!hasUsedTrial && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 inline-flex items-center gap-2 bg-gradient-to-r from-secondary/20 to-primary/20 px-6 py-3 rounded-full border border-secondary/30"
            >
              <Gift className="w-5 h-5 text-secondary" />
              <span className="font-heading font-bold text-secondary">7-DAY FREE TRIAL</span>
              <span className="text-muted-foreground">on all tiers • Card required</span>
            </motion.div>
          )}
          
          <p className="text-sm text-muted-foreground mt-4">
            All artist tiers require completing your profile with social media links (Instagram, YouTube, + one more)
          </p>
        </div>

        {/* Important Notice */}
        <Card className="glass-card border-secondary/30 p-6 mb-8 max-w-3xl mx-auto">
          <h3 className="font-heading text-lg font-bold uppercase mb-3 flex items-center gap-2">
            <Shield className="w-5 h-5 text-secondary" />
            Platform Requirements
          </h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" />
              <span><strong>Real Artists Only:</strong> AI-generated artists are NOT allowed. AI tools may assist, but you must be a real human creator.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" />
              <span><strong>DSP-Eligible Music:</strong> Only music eligible for digital streaming platforms is allowed.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" />
              <span><strong>Required Social Links:</strong> Instagram + YouTube + at least one additional platform.</span>
            </li>
          </ul>
        </Card>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tiers.map((tier, index) => {
            const IconComponent = TIER_ICONS[tier.tier_id] || Star;
            const colorClass = TIER_COLORS[tier.tier_id] || 'bg-primary';
            const isPopular = tier.tier_id === 'pro';
            const isEnterprise = tier.tier_id === 'label';
            
            return (
              <motion.div
                key={tier.tier_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card 
                  className={`glass-card p-6 relative h-full flex flex-col ${
                    isPopular ? 'neon-border scale-105 z-10' : 'border-border'
                  } ${isEnterprise ? 'border-pink-500/30' : ''}`}
                >
                  {isPopular && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-secondary">
                      Most Popular
                    </Badge>
                  )}
                  {isEnterprise && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-pink-500">
                      Enterprise
                    </Badge>
                  )}

                  <div className="text-center mb-6">
                    <div className={`w-12 h-12 rounded-full ${colorClass} flex items-center justify-center mx-auto mb-4`}>
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <h3 className="font-heading text-xl font-bold uppercase mb-2">{tier.name}</h3>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="font-heading text-4xl font-black">${tier.price}</span>
                      <span className="text-muted-foreground">/mo</span>
                    </div>
                    {!hasUsedTrial && (
                      <p className="text-xs text-secondary mt-2">
                        <Gift className="w-3 h-3 inline mr-1" />
                        7-day free trial
                      </p>
                    )}
                  </div>

                  <ul className="space-y-2 mb-6 flex-1">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <CheckCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                          tier.tier_id === 'elite' ? 'text-accent' : 
                          tier.tier_id === 'pro' ? 'text-secondary' : 
                          'text-primary'
                        }`} />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                    
                    {/* Additional details */}
                    <li className="flex items-start gap-2 text-xs text-muted-foreground pt-2 border-t border-border mt-2">
                      <span>Track limit: {tier.track_limit === -1 ? 'Unlimited' : tier.track_limit}</span>
                    </li>
                    <li className="flex items-start gap-2 text-xs text-muted-foreground">
                      <span>Channels: {tier.channel_limit === -1 ? 'Unlimited' : tier.channel_limit === 0 ? 'None' : tier.channel_limit}</span>
                    </li>
                    <li className="flex items-start gap-2 text-xs text-muted-foreground">
                      <span>Platform fee: {tier.platform_fee_percent}%</span>
                    </li>
                  </ul>

                  <Button
                    className={`w-full ${
                      tier.tier_id === 'elite' 
                        ? 'bg-accent hover:bg-accent/90 text-background' 
                        : tier.tier_id === 'pro'
                          ? 'bg-secondary hover:bg-secondary/90' 
                          : tier.tier_id === 'label'
                            ? 'bg-pink-500 hover:bg-pink-500/90'
                            : 'bg-primary hover:bg-primary/90'
                    }`}
                    onClick={() => handleSubscribeClick(tier.tier_id)}
                    disabled={loading === tier.tier_id || isCurrentTier(tier.tier_id) || (user?.subscription_active && !isHigherTier(tier.tier_id) && !isCurrentTier(tier.tier_id))}
                    data-testid={`subscribe-${tier.tier_id}-btn`}
                  >
                    {loading === tier.tier_id ? 'Processing...' : 
                     isCurrentTier(tier.tier_id) ? 'Current Plan' :
                     user?.subscription_active && isHigherTier(tier.tier_id) ? `Upgrade to ${tier.name}` :
                     user?.subscription_active && !isHigherTier(tier.tier_id) ? 'Lower Tier' :
                     hasUsedTrial || user?.artist_tier ? `Get ${tier.name}` : 'Start Free Trial'}
                    {!isCurrentTier(tier.tier_id) && (isHigherTier(tier.tier_id) || !user?.subscription_active) && <ArrowRight className="ml-2 w-4 h-4" />}
                  </Button>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Trial Terms Modal */}
        <AnimatePresence>
          {showTrialModal && selectedTierData && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setShowTrialModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="w-full max-w-lg"
                onClick={e => e.stopPropagation()}
              >
                <Card className="glass-card border-border p-6">
                  <div className="text-center mb-6">
                    {!hasUsedTrial && (
                      <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-4">
                        <Gift className="w-8 h-8 text-secondary" />
                      </div>
                    )}
                    <h2 className="font-heading text-2xl font-bold uppercase mb-2">
                      {user?.subscription_active && isHigherTier(selectedTier) ? `Upgrade to ${selectedTierData.name}` :
                       hasUsedTrial ? `Subscribe to ${selectedTierData.name}` : 'Start Your Free Trial'}
                    </h2>
                    <p className="text-muted-foreground">
                      {user?.subscription_active && isHigherTier(selectedTier) 
                        ? `${selectedTierData.name} - $${selectedTierData.price}/month`
                        : hasUsedTrial 
                        ? `${selectedTierData.name} - $${selectedTierData.price}/month`
                        : `${selectedTierData.name} - $${selectedTierData.price}/month after trial`
                      }
                    </p>
                  </div>

                  {!hasUsedTrial && (
                    <Card className="bg-secondary/10 border-secondary/30 p-4 mb-6">
                      <div className="flex items-center gap-3 mb-2">
                        <Calendar className="w-5 h-5 text-secondary" />
                        <span className="font-bold">7-Day Free Trial</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Your card will NOT be charged today. Billing begins automatically after the 7-day trial ends.
                      </p>
                    </Card>
                  )}

                  <Card className="bg-muted/30 border-border p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div className="text-sm">
                        <p className="font-bold mb-2">Billing Agreement</p>
                        <p className="text-muted-foreground text-xs">
                          I understand that by starting this {hasUsedTrial ? 'subscription' : 'free trial'}, I am authorizing DMV Soundstage to 
                          {hasUsedTrial 
                            ? ' charge my card for the subscription amount.' 
                            : ' automatically charge my card after the 7-day trial period ends.'
                          } I understand that if my payment fails due to insufficient funds or other issues, 
                          DMV Soundstage will continue attempting to collect payment until the balance is resolved 
                          or my subscription is canceled according to the platform's billing policy.
                        </p>
                      </div>
                    </div>
                  </Card>

                  <div className="flex items-start gap-3 mb-6">
                    <Checkbox
                      id="trial-agreement"
                      checked={agreedToTerms}
                      onCheckedChange={setAgreedToTerms}
                      data-testid="trial-agreement-checkbox"
                    />
                    <label htmlFor="trial-agreement" className="text-sm cursor-pointer">
                      I have read and agree to the billing terms above. I understand that my card will be charged ${selectedTierData.price} 
                      {!hasUsedTrial && ' after the 7-day trial'} and automatically each month until I cancel.
                    </label>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setShowTrialModal(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="flex-1 bg-secondary hover:bg-secondary/90"
                      onClick={handleConfirmSubscription}
                      disabled={!agreedToTerms || loading}
                      data-testid="confirm-subscription-btn"
                    >
                      {loading ? 'Processing...' : hasUsedTrial ? 'Subscribe Now' : 'Start Free Trial'}
                    </Button>
                  </div>
                </Card>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Listeners Notice */}
        <Card className="glass-card border-border p-8 mt-12 max-w-2xl mx-auto text-center">
          <Users className="w-12 h-12 mx-auto mb-4 text-primary" />
          <h3 className="font-heading text-2xl font-bold uppercase mb-2">Listeners Stay Free</h3>
          <p className="text-muted-foreground mb-4">
            Stream unlimited music, request tracks, follow artists, comment, and RSVP to events — all for free.
          </p>
          <Button variant="outline" onClick={() => navigate('/login')} data-testid="listener-signup-btn">
            Create Free Listener Account
          </Button>
        </Card>

        {/* FAQ Section */}
        <div className="mt-16">
          <h2 className="font-heading text-2xl font-bold uppercase mb-8 text-center">Frequently Asked Questions</h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <Card className="glass-card border-border p-6">
              <h3 className="font-bold mb-2">How does the free trial work?</h3>
              <p className="text-sm text-muted-foreground">You get 7 days free with full access to your chosen tier. A valid card is required, but won't be charged until after the trial. Cancel anytime before the trial ends to avoid charges.</p>
            </Card>
            <Card className="glass-card border-border p-6">
              <h3 className="font-bold mb-2">What happens after my trial ends?</h3>
              <p className="text-sm text-muted-foreground">Your card will be automatically charged for your subscription. You'll continue to have full access as long as your subscription is active.</p>
            </Card>
            <Card className="glass-card border-border p-6">
              <h3 className="font-bold mb-2">What social links are required?</h3>
              <p className="text-sm text-muted-foreground">All artists must provide Instagram, YouTube, and at least one additional platform (Twitter, TikTok, Facebook, SoundCloud, Spotify, or Apple Music).</p>
            </Card>
            <Card className="glass-card border-border p-6">
              <h3 className="font-bold mb-2">Can I cancel during the trial?</h3>
              <p className="text-sm text-muted-foreground">Yes! You can cancel anytime during your 7-day trial. Your card won't be charged, and access ends immediately upon cancellation.</p>
            </Card>
            <Card className="glass-card border-border p-6">
              <h3 className="font-bold mb-2">What if my payment fails?</h3>
              <p className="text-sm text-muted-foreground">We'll retry the payment automatically. During this time, your uploads will be restricted but your existing content stays visible. Access is restored once payment succeeds.</p>
            </Card>
            <Card className="glass-card border-border p-6">
              <h3 className="font-bold mb-2">Can I upgrade my tier later?</h3>
              <p className="text-sm text-muted-foreground">Yes! You can upgrade at any time. The new features are available immediately, and pricing adjusts on your next billing date.</p>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
